import React from "react";
import {
    Box,
    Button,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

const safeStr = (value) => (value == null ? "" : String(value));
const ensureArray = (value) => (Array.isArray(value) ? value : []);
const DEFAULT_IDS = ["А", "Б", "В", "Г", "Д", "Е"];

const normalizeItems = (items) =>
    ensureArray(items).map((item, idx) => ({
        id: safeStr(item?.id || DEFAULT_IDS[idx] || idx + 1),
        text: safeStr(item?.text),
    }));

const getNormalizedOrder = (items, order) => {
    const itemIds = items.map((item) => item.id).filter(Boolean);
    const selectedOrder = ensureArray(order).map(safeStr).filter((id) => itemIds.includes(id));
    const missingIds = itemIds.filter((id) => !selectedOrder.includes(id));
    return [...selectedOrder, ...missingIds];
};

const QuestionFormLogicalSequence = ({ value, onChange }) => {
    const v = value || {};
    const items = normalizeItems(v.items);
    const correctOrder = getNormalizedOrder(items, v.correctOrder);

    const updateItems = (nextItems) => {
        onChange({
            ...v,
            items: nextItems,
            correctOrder: getNormalizedOrder(nextItems, correctOrder),
        });
    };

    const updateItem = (idx, patch) => {
        updateItems(items.map((item, itemIdx) => (
            itemIdx === idx ? { ...item, ...patch } : item
        )));
    };

    const addItem = () => {
        const nextId = DEFAULT_IDS.find((id) => !items.some((item) => item.id === id)) || String(items.length + 1);
        updateItems([...items, { id: nextId, text: "" }]);
    };

    const removeItem = (idx) => {
        updateItems(items.filter((_, itemIdx) => itemIdx !== idx));
    };

    const setCorrectOrderAt = (idx, id) => {
        const nextOrder = [...correctOrder];
        const otherIdx = nextOrder.indexOf(id);
        if (otherIdx >= 0) {
            nextOrder[otherIdx] = nextOrder[idx];
        }
        nextOrder[idx] = id;
        onChange({ ...v, correctOrder: nextOrder });
    };

    const moveCorrectOrder = (idx, dir) => {
        const nextIdx = idx + dir;
        if (nextIdx < 0 || nextIdx >= correctOrder.length) return;

        const nextOrder = [...correctOrder];
        [nextOrder[idx], nextOrder[nextIdx]] = [nextOrder[nextIdx], nextOrder[idx]];
        onChange({ ...v, correctOrder: nextOrder });
    };

    return (
        <Stack spacing={2}>
            <Typography variant="subtitle2">Логічна послідовність</Typography>

            <TextField
                label="Підзаголовок"
                value={v.subtitle || ""}
                onChange={(e) => onChange({ ...v, subtitle: e.target.value })}
                fullWidth
            />

            <TextField
                label="Текст завдання"
                value={v.question || ""}
                onChange={(e) => onChange({ ...v, question: e.target.value })}
                fullWidth
                multiline
                minRows={2}
            />

            <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2">Пункти</Typography>
                    <Button size="small" startIcon={<AddIcon />} onClick={addItem}>
                        Додати пункт
                    </Button>
                </Stack>

                <Stack spacing={1}>
                    {items.map((item, idx) => (
                        <Stack key={`${item.id}-${idx}`} direction={{ xs: "column", sm: "row" }} spacing={1}>
                            <TextField
                                label="ID"
                                value={item.id}
                                onChange={(e) => updateItem(idx, { id: e.target.value })}
                                sx={{ width: { xs: "100%", sm: 110 } }}
                            />
                            <TextField
                                label="Текст"
                                value={item.text}
                                onChange={(e) => updateItem(idx, { text: e.target.value })}
                                fullWidth
                                multiline
                            />
                            <IconButton
                                aria-label="Видалити пункт"
                                onClick={() => removeItem(idx)}
                                disabled={items.length <= 2}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Stack>
                    ))}
                </Stack>
            </Box>

            <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Правильна послідовність
                </Typography>

                <Stack spacing={1}>
                    {correctOrder.map((id, idx) => (
                        <Stack key={`${id}-${idx}`} direction="row" spacing={1} alignItems="center">
                            <Typography sx={{ width: 28 }}>{idx + 1}</Typography>
                            <TextField
                                select
                                label="Пункт"
                                value={id}
                                onChange={(e) => setCorrectOrderAt(idx, e.target.value)}
                                fullWidth
                            >
                                {items.map((item) => (
                                    <MenuItem key={item.id} value={item.id}>
                                        {item.id} {item.text ? `- ${item.text.slice(0, 60)}` : ""}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <IconButton aria-label="Вище" onClick={() => moveCorrectOrder(idx, -1)} disabled={idx === 0}>
                                <ArrowUpwardIcon />
                            </IconButton>
                            <IconButton aria-label="Нижче" onClick={() => moveCorrectOrder(idx, 1)} disabled={idx === correctOrder.length - 1}>
                                <ArrowDownwardIcon />
                            </IconButton>
                        </Stack>
                    ))}
                </Stack>
            </Box>
        </Stack>
    );
};

export default QuestionFormLogicalSequence;
